import { AsyncCallStatus, useAsync } from './use-async';
import { OktaAuth, Tokens } from '@okta/okta-auth-js';
import { useCallback } from 'react';

export type SessionStateAndTokens = {
  exists?: boolean;
  tokens?: Tokens;
};

type Optional<T> = T | null | undefined;

// eslint-disable-next-line @typescript-eslint/ban-types
export type CheckOktaSessionState<ErrorType = {}> = {
  error: Optional<ErrorType>;
  status: AsyncCallStatus;
  sessionStateAndTokens: Optional<SessionStateAndTokens>;
};
// eslint-disable-next-line @typescript-eslint/ban-types
const useCheckOktaSession = <ErrorType = {}>(
  oktaAuth: Optional<OktaAuth>,
  refreshTokens = false,
): CheckOktaSessionState<ErrorType> => {
  const checkSessionFn =
    useCallback(async (): Promise<SessionStateAndTokens> => {
      if (!oktaAuth) {
        return Promise.resolve({
          exists: false,
        });
      } else {
        return await oktaAuth.session.exists().then((exists) => {
          if (refreshTokens && exists) {
            return oktaAuth.token.getWithoutPrompt().then((token) => {
              return {
                exists,
                tokens: token.tokens,
              };
            });
          } else {
            return {
              exists,
              tokens: oktaAuth.tokenManager.getTokensSync(),
            };
          }
        });
      }
    }, [refreshTokens, oktaAuth]);
  const {
    error = undefined,
    execute: _execute,
    status,
    value,
  } = useAsync<SessionStateAndTokens, ErrorType>(checkSessionFn, true);
  return {
    error,
    status,
    sessionStateAndTokens: value,
  };
};

export { useCheckOktaSession };
